import { NonIdealState, Spinner, H4 } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import usePrevious from 'react-use/lib/usePrevious';
import styled from 'styled-components';
import useUserContext from 'ecommerce/context/user';
import ancestorsApi from '../../api/ancestors';

import useEditorItemContext from '../../context/editorItemContext';
import PersonCardError from './personCardError';
import PersonCardSelect from './personCardSelect';
import { Typography } from '../../components/base';

const StyledPersonCardSelect = styled(PersonCardSelect)`
  margin-bottom: 20px;
`;

const StyledPersonCardError = styled(PersonCardError)`
  margin-bottom: 20px;
`;

const StyledNonIdealState = styled(NonIdealState)`
  margin-bottom: 20px;
`;

const fetchPartners = async (type, accessToken, personId, signal) => {
  const { data, error } = await ancestorsApi(type, accessToken).getPartners(personId, { signal });
  if (error) {
    throw error;
  }
  return data;
};

const PersonCardCoupleSelect = ({
  onPartnerSelect,
  onSelect,
  partnerRequired,
  people,
  selectedPartner,
  selectedPerson,
  ...props
}) => {
  const { accessToken } = useUserContext();
  // editorItemContext can be undefined, because this component can be used outside of the SKU editor. (mycanvas family tree)
  const editorItemContext = useEditorItemContext();

  const [abortController, setAbortController] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnersError, setPartnersError] = useState();

  const previousSelectedPerson = usePrevious(selectedPerson);

  const visiblePeople = useMemo(() => (selectedPerson == null ? people : [selectedPerson]), [
    people,
    selectedPerson,
  ]);

  const visiblePartners = useMemo(() => (selectedPartner == null ? partners : [selectedPartner]), [
    partners,
    selectedPartner,
  ]);

  const allPeople = useMemo(() => {
    if (props.allPeople) {
      return props.allPeople;
    }
    return people;
  }, [people, props.allPeople]);

  const partnerNode = useMemo(() => {
    if (selectedPerson == null) {
      return null;
    }
    if (isLoading) {
      return (
        <StyledNonIdealState
          icon={<Spinner />}
          description="Searching for partners, please wait."
        />
      );
    }
    if (partnersError) {
      return <StyledPersonCardError message={partnersError.message} />;
    }
    if (partners.length === 0) {
      if (partnerRequired) {
        return (
          <StyledNonIdealState
            description="Selected person has no partners. Page can't be added."
            icon="error"
            title="No partner found"
          />
        );
      }

      // There is no partners and partner is optional
      return null;
    }

    return (
      <>
        <H4>
          <span>Select a partner you would like to include in this project</span>
          {!partnerRequired && <strong> (optional)</strong>}
        </H4>
        <StyledPersonCardSelect
          onSelect={onPartnerSelect}
          people={visiblePartners}
          selectedPerson={selectedPartner}
        />
      </>
    );
  }, [
    isLoading,
    onPartnerSelect,
    partnerRequired,
    partners,
    partnersError,
    selectedPartner,
    selectedPerson,
    visiblePartners,
  ]);

  // Abort the current controller on unmount component, or when another controller is set.
  useEffect(() => () => abortController?.abort(), [abortController]);

  useEffect(() => {
    // Use this effect only if selected person changes.
    if (previousSelectedPerson?.id === selectedPerson?.id) {
      return;
    }
    // Abort the current controller and prepare a new one.
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    // Unselect partner whenever person changes.
    onPartnerSelect({ person: undefined });
    // If person was unselected, reset the partner state.
    if (selectedPerson == null) {
      setPartnersError();
      setIsLoading(false);
      setPartners([]);
      return;
    }
    // If person was selected, start fetching his partners.
    setIsLoading(true);
    (async () => {
      if (selectedPerson.pids) {
        const newPartners = allPeople.filter(p => selectedPerson.pids.indexOf(p.id) >= 0);
        setIsLoading(false);
        setPartners(newPartners);
        // If there is only one partner and he/she is required to be selected, do it automatically.
        if (partnerRequired && newPartners.length === 1) {
          onPartnerSelect({ person: newPartners[0] });
        }
        return;
      }

      try {
        const newPartners = await fetchPartners(
          editorItemContext?.config?.source,
          accessToken,
          selectedPerson.id,
          newAbortController.signal,
        );
        setPartnersError();
        setIsLoading(false);
        setPartners(newPartners);
        // If there is only one partner and he/she is required to be selected, do it automatically.
        if (partnerRequired && newPartners.length === 1) {
          onPartnerSelect({ person: newPartners[0] });
        }
      } catch (error) {
        // When abort() is called, the fetch() promise rejects with an AbortError.
        // It's ok, don't do anything, component is unmounted.
        if (error.name === 'AbortError') {
          return;
        }
        console.error(error);
        setPartnersError(error);
        setIsLoading(false);
      }
    })();
  }, [
    accessToken,
    onPartnerSelect,
    partnerRequired,
    previousSelectedPerson,
    selectedPerson,
    allPeople,
  ]);

  return (
    <>
      <StyledPersonCardSelect
        onSelect={onSelect}
        people={visiblePeople}
        selectedPerson={selectedPerson}
      />
      {partnerNode}
    </>
  );
};

PersonCardCoupleSelect.defaultProps = {
  onPartnerSelect: () => { },
  onSelect: () => { },
  partnerRequired: false,
  people: [],
  allPeople: undefined,
  selectedPartner: undefined,
  selectedPerson: undefined,
};

PersonCardCoupleSelect.propTypes = {
  onPartnerSelect: PropTypes.func,
  onSelect: PropTypes.func,
  partnerRequired: PropTypes.bool,
  people: PropTypes.arrayOf(PropTypes.object),
  allPeople: PropTypes.arrayOf(PropTypes.object),
  selectedPartner: PropTypes.object,
  selectedPerson: PropTypes.object,
};

export default PersonCardCoupleSelect;
